import { useCreateEnvironmentAccessToken, useEnvironment } from "@/queries/environment-queries";
import { createPromiseClient, type PromiseClient } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { useMemo } from "react";
import { EnvironmentOpsService } from "gitpod-next-api/supervisor/v1/environmentopts_connect";

export type EnvironmentOpsServiceClient = PromiseClient<typeof EnvironmentOpsService>;

export const useEnvironmentOpsService = (environmentId: string): EnvironmentOpsServiceClient | null => {
    const { data: environment } = useEnvironment(environmentId);
    const { data: environmentAccessToken } = useCreateEnvironmentAccessToken(environment?.id);
    const transport = useMemo(() => {
        const opsUrlString = environment?.status?.environmentUrls?.ops;
        const token = environmentAccessToken;
        if (!opsUrlString || !token) {
            return null;
        }

        const supervisorUrl = new URL(opsUrlString);
        // Keep path for devrunner
        if (!supervisorUrl.pathname.includes("/supervisor/")) {
            // TODO: Remove this once no active orgs have runners with version between october 2025 and february 2026
            // https://github.com/gitpod-io/gitpod-next/pull/14156
            supervisorUrl.pathname = "";
        }

        return createConnectTransport({
            baseUrl: supervisorUrl.toString(),
            useBinaryFormat: false,
            interceptors: [
                (next) => async (req) => {
                    req.header.set("Authorization", `Bearer ${token}`);
                    return await next(req);
                },
            ],
            defaultTimeoutMs: 30 * 1000,
        });
    }, [environmentAccessToken, environment]);
    return useMemo(() => (transport ? createPromiseClient(EnvironmentOpsService, transport) : null), [transport]);
};
